import React from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Briefcase, Users, Clock, Target, TrendingUp, FileText, CheckCircle, ArrowRight, Award, Shield, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../mock';
import SEO from '../components/SEO';

const Services = () => {
  const iconMap = {
    briefcase: Briefcase,
    users: Users,
    clock: Clock,
    target: Target,
    'trending-up': TrendingUp,
    'file-text': FileText,
    award: Award,
    shield: Shield,
    'refresh-cw': RefreshCw
  };

  return (
    <div>
      <SEO 
        title="Services"
        description="Comprehensive staffing and recruitment services including executive search, permanent placement, temporary staffing, contract staffing, RPO, and talent advisory. Munoret delivers the right talent at the right time."
        keywords="executive search services, permanent placement, temporary staffing, contract staffing, RPO, recruitment process outsourcing, talent advisory, staffing solutions, Munoret services"
      />
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(180deg, var(--bg-light) 0%, var(--bg-primary) 100%)',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <motion.h1 
            className="display-hero"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '24px' }}
          >
            Tailored Staffing Solutions
          </motion.h1>
          <motion.p 
            className="body-large"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}
          >
            From executive leadership to temporary coverage, our comprehensive services are designed to meet your unique talent needs.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'grid',
            gap: '48px'
          }}>
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '48px',
                    alignItems: 'center',
                    padding: '48px',
                    background: isEven ? 'var(--bg-light)' : 'white',
                    borderRadius: '24px',
                    border: '1px solid var(--border-medium)'
                  }}
                >
                  <div>
                    <div style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '16px',
                      background: isEven ? 'white' : 'var(--bg-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px'
                    }}>
                      <IconComponent size={36} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                    <h3 className="heading-large" style={{ marginBottom: '16px' }}>
                      {service.title}
                    </h3>
                    <p className="body-large" style={{ 
                      color: 'var(--text-secondary)',
                      lineHeight: '1.6'
                    }}>
                      {service.description}
                    </p>
                  </div>

                  <div>
                    <h4 style={{ 
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '20px',
                      color: 'var(--text-primary)'
                    }}>
                      Key Features:
                    </h4>
                    <ul style={{ 
                      listStyle: 'none',
                      padding: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {service.features.map((feature, idx) => (
                        <li key={idx} style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}>
                          <CheckCircle size={20} style={{ 
                            color: 'var(--brand-primary)',
                            flexShrink: 0,
                            marginTop: '2px'
                          }} />
                          <span style={{ color: 'var(--text-primary)' }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section style={{ padding: '80px 0', background: 'var(--bg-light)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <h2 className="heading-large" style={{ marginBottom: '16px' }}>
              Our Proven Process
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              A systematic approach that delivers results
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                step: '01',
                title: 'Consultation',
                description: 'Deep dive into your needs, culture, and requirements'
              },
              {
                step: '02',
                title: 'Sourcing',
                description: 'Leverage our network and tools to find top candidates'
              },
              {
                step: '03',
                title: 'Screening',
                description: 'Rigorous vetting process including interviews and assessments'
              },
              {
                step: '04',
                title: 'Presentation',
                description: 'Present only the best-fit candidates for your review'
              },
              {
                step: '05',
                title: 'Support',
                description: 'Ongoing support through onboarding and beyond'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                style={{
                  background: 'white',
                  padding: '32px',
                  borderRadius: '16px',
                  border: '1px solid var(--border-medium)',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--brand-primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  fontWeight: '600',
                  margin: '0 auto 20px'
                }}>
                  {item.step}
                </div>
                <h3 className="heading-medium" style={{ marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-subtle" style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '800px', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-large" style={{ marginBottom: '24px' }}>
              Let's Build Your Dream Team
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              marginBottom: '40px'
            }}>
              Schedule a consultation to discuss your specific staffing needs.
            </p>
            <Link to="/contact" className="btn-primary" style={{ gap: '8px' }}>
              Contact Us Today
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
